import React, { useState, useEffect } from "react";
import useSWR from "swr";
import DashboardContentHeader from "./DashboardContentHeader";
import DashboardContenLeftMenus from "./DashboardContenLeftMenus";
import TopActionMenus from "./TopActionMenus";
import DashboardContentFooter from "./DashboardContentFooter";
import { faArrowLeft } from "@fortawesome/free-solid-svg-icons";
import { useNavigationEvent } from "navigation-react";
import SecureLS from "secure-ls";

const ls = new SecureLS({ encodingType: "aes" });
const API_BASE = process.env.REACT_APP_DRUPAL_BASE_URL;

// SWR fetcher
const fetcher = (url) =>
  fetch(url, { credentials: "include" }).then((res) => {
    if (!res.ok) throw new Error(`Fetch error ${res.status}`);
    return res.json();
  });

// Auth headers
const getAuthHeaders = () => {
  const csrfToken = ls.get("usercsrf_token");
  if (!csrfToken) throw new Error("No CSRF token found");
  return {
    "Content-Type": "application/vnd.api+json",
    Accept: "application/vnd.api+json",
    "X-CSRF-Token": csrfToken,
  };
};

// Convert file to Base64
const fileToBase64 = (file) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => resolve(reader.result.split(",")[1]); // remove prefix
    reader.onerror = (err) => reject(err);
  });

export default function StaffEditRegister() {
  const { stateNavigator, data } = useNavigationEvent();

  const [firstName, setFirstName] = useState(data?.firstName || "");
  const [lastName, setLastName] = useState(data?.lastName || "");
  const [email, setEmail] = useState(data?.email || "");
  const [phone, setPhone] = useState(data?.phone || "");
  const [department, setDepartment] = useState(data?.departmemtUUID || "");
  const [statusID, setStatusID] = useState(Number(data?.statusID) || 1);
  const [staffID, setStaffID] = useState(data?.staffID || "");
  const [staffPicture, setStaffPicture] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const DEPARTMENT_API = `${API_BASE}/jsonapi/taxonomy_term/staff_department`;
  const STAFF_PATCH_API = (uuid) => `${API_BASE}/jsonapi/user/user/${uuid}`;
  const FILE_UPLOAD_API = `${API_BASE}/jsonapi/file/file`;

  const { data: departmentData } = useSWR(DEPARTMENT_API, fetcher);
  const departmentItems =
    departmentData?.data?.map((term) => ({
      label: term.attributes.name,
      value: term.id,
    })) || [];

  useEffect(() => {
    const csrfToken = ls.get("usercsrf_token");
    const username = ls.get("username");
    if (!csrfToken || !username) {
      alert("You are not logged in. Redirecting to login page.");
      stateNavigator.navigate("login");
    }
  }, [stateNavigator]);

  // Upload staff picture to Drupal JSON:API
  const uploadStaffPicture = async (file) => {
    if (!file) return null;

    const headers = getAuthHeaders();
    const base64Data = await fileToBase64(file);

    const payload = {
      data: {
        type: "file--file",
        attributes: {
          filename: file.name,
          data: base64Data,
        },
      },
    };

    const res = await fetch(FILE_UPLOAD_API, {
      method: "POST",
      headers,
      credentials: "include",
      body: JSON.stringify(payload),
    });

    if (!res.ok) {
      const errText = await res.text();
      throw new Error(`File upload failed: ${errText}`);
    }

    const json = await res.json();
    return json.data.id; // UUID of uploaded file
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!firstName || !lastName || !department) {
      alert("Please fill all required fields");
      return;
    }

    setIsSubmitting(true);

    try {
      const headers = getAuthHeaders();

      const payload = {
        data: {
          type: "user--user",
          id: data?.staffUUID,
          attributes: {
            field_staff_id: staffID,
            field_firist_name: firstName,
            field_last_name: lastName,
            field_email_address: email,
            field_phone_number: phone,
            status: statusID,
          },
          relationships: {
            field_department: {
              data: { type: "taxonomy_term--staff_department", id: department },
            },
          },
        },
      };

      // Upload file if selected
      if (staffPicture) {
        const fileID = await uploadStaffPicture(staffPicture);
        payload.data.relationships.field_staff_picture = {
          data: { type: "file--file", id: fileID },
        };
      }

      const response = await fetch(STAFF_PATCH_API(data?.staffUUID), {
        method: "PATCH",
        headers,
        body: JSON.stringify(payload),
        credentials: "include",
      });

      if (!response.ok) {
        const errText = await response.text();
        throw new Error(`Failed to update staff: ${errText}`);
      }

      const result = await response.json();
      console.log("Staff updated successfully:", result);
      alert("Staff updated successfully");
      stateNavigator.navigate("staffregister");
    } catch (err) {
      console.error("Error updating staff profile:", err);
      alert(`Error updating staff profile: ${err.message}`);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="dashboard-container">
      <DashboardContentHeader />
      <div className="content-wrapper">
        <DashboardContenLeftMenus />
        <div className="patient-register">
          <TopActionMenus
            AddTitle="Edit Staff details"
            backIcon={faArrowLeft}
            backNavigationLink="staffregister"
          />
          <form
            className="patient-form"
            onSubmit={handleSubmit}
            style={{ width: "60%", margin: "0 auto" }}
          >
              <div className="form-group">
              <label>Staff ID</label>
              <input
                type="text"
                value={staffID}
                maxLength={4}
                onChange={(e) => setStaffID(e.target.value)}
              />
            </div> 

            <div className="form-group">
              <label>First Name</label>
              <input
                type="text"
                value={firstName}
                onChange={(e) => setFirstName(e.target.value)}
                required
              />
            </div>

            <div className="form-group">
              <label>Last Name</label>
              <input
                type="text"
                value={lastName}
                onChange={(e) => setLastName(e.target.value)}
                required
              />
            </div>

            <div className="form-group">
              <label>Email Address</label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
              />
            </div>

            <div className="form-group">
              <label>Phone Number</label>
              <input
                type="tel"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
              />
            </div>

            <div className="form-group">
              <label>Department</label>
              <select
                value={department}
                onChange={(e) => setDepartment(e.target.value)}
                required
              >
                <option value="">Select Department</option>
                {departmentItems.map((item) => (
                  <option key={item.value} value={item.value}>
                    {item.label}
                  </option>
                ))}
              </select>
            </div>

            <div className="form-group">
              <label>Staff Picture</label>
              <input
                type="file"
                onChange={(e) => setStaffPicture(e.target.files[0])}
              />
            </div>

            <div className="form-group">
              <label>Status</label>
              <select
                value={statusID}
                onChange={(e) => setStatusID(Number(e.target.value))}
                required
              >
                <option value="">Select Status</option>
                <option value={1}>Active</option>
                <option value={0}>Inactive</option>
              </select>
            </div>

            <button type="submit" disabled={isSubmitting}>
              {isSubmitting ? "Updating..." : "Update Staff"}
            </button>
          </form>
        </div>
      </div>
      <DashboardContentFooter />
    </div>
  );
}
